import { ApiClientError } from '../api/client';

export type FieldName = 'email' | 'password' | 'displayName' | 'name';
const KNOWN_FIELDS: readonly FieldName[] = ['email', 'password', 'displayName', 'name'];

export type FormErrorResult = {
  fieldErrors: Partial<Record<FieldName, string>>;
  formError?: string;
};

/**
 * Turns a mutation error into field-level and/or form-level feedback.
 *
 * - `fieldCodes` maps a specific `ApiClientError.code` straight to a field (e.g.
 *   `{ email_taken: 'email' }`), for errors whose field is implied by the code itself.
 * - `code === 'validation'` messages of the form "Invalid field: <field>" are parsed and
 *   attached to that field when it's one of the known, server-validated fields.
 * - Everything else (unmapped codes, non-validation 4xx/5xx, or a non-ApiClientError such
 *   as a network failure) falls back to a single form-level error — no error is ever
 *   silently dropped.
 */
export function deriveFormError(
  error: unknown,
  fieldCodes: Partial<Record<string, FieldName>> = {},
): FormErrorResult {
  if (!error) return { fieldErrors: {} };

  if (error instanceof ApiClientError) {
    const mappedField = fieldCodes[error.code];
    if (mappedField) return { fieldErrors: { [mappedField]: error.message } };

    if (error.code === 'validation') {
      const match = /Invalid field:\s*(\w+)/i.exec(error.message);
      const field = match?.[1] as FieldName | undefined;
      if (field && KNOWN_FIELDS.includes(field)) {
        return { fieldErrors: { [field]: error.message } };
      }
    }
  }

  const message = error instanceof Error ? error.message : undefined;
  return {
    fieldErrors: {},
    formError: `Something went wrong — please try again.${message ? ` ${message}` : ''}`,
  };
}
